import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  canUseVertex360Copilot,
  SP_SETTINGS_AI_UPDATED_EVENT,
} from "helpers/spAiFeatureAccess";

/** Dispatched after Pusher role_update → get-role-authenticate-user (auth.sagas.js). */
export const REFRESH_PERMISSIONS_EVENT = "refreshPermissions";

/**
 * Org + role Copilot gate for /copilot and the floating assistant.
 *
 * Re-reads localStorage on every render that is triggered by:
 *   - Redux user / permissions change (login, logout, role_update)
 *   - Pusher events (SP_SETTINGS_AI_UPDATED_EVENT, REFRESH_PERMISSIONS_EVENT)
 *   - Cross-tab storage events
 *
 * canUseVertex360Copilot() only reads localStorage — fast, no need for memoisation.
 */
export const useVertex360CopilotAccess = () => {
  const reduxPermissions = useSelector((state) =>
    state?.users?.getIn?.(["user", "permissions"])
  );
  const reduxUserId = useSelector((state) =>
    state?.users?.getIn?.(["user", "id"])
  );

  // gateVersion exists solely to force a re-render when external events fire
  // (Pusher, cross-tab storage). The selectors above handle Redux-driven re-renders.
  const [gateVersion, setGateVersion] = useState(0);

  const bumpGate = useCallback(() => {
    setGateVersion((value) => value + 1);
  }, []);

  useEffect(() => {
    window.addEventListener(SP_SETTINGS_AI_UPDATED_EVENT, bumpGate);
    window.addEventListener(REFRESH_PERMISSIONS_EVENT, bumpGate);
    window.addEventListener("storage", bumpGate);
    return () => {
      window.removeEventListener(SP_SETTINGS_AI_UPDATED_EVENT, bumpGate);
      window.removeEventListener(REFRESH_PERMISSIONS_EVENT, bumpGate);
      window.removeEventListener("storage", bumpGate);
    };
  }, [bumpGate]);

  // When permissions arrive after Pusher role_update, force a re-render.
  useEffect(() => {
    if (reduxPermissions != null) {
      bumpGate();
    }
  }, [reduxPermissions, bumpGate]);

  // When the user identity changes (login / switch provider), force a re-render
  // so the gate is re-checked after all localStorage writes have completed.
  useEffect(() => {
    if (reduxUserId != null) {
      bumpGate();
    }
  }, [reduxUserId, bumpGate]);

  // Evaluate fresh on every render — no useMemo caching.
  // References to gateVersion / selectors above ensure this component re-renders
  // whenever any gate input might have changed.
  void gateVersion;
  void reduxPermissions;
  void reduxUserId;
  return canUseVertex360Copilot();
};

export default useVertex360CopilotAccess;
